import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { ISlider } from "../models/ISlider";
import { getSliders, postSlider, putSlider, deleteSlider } from "../services/slider";

const EMPTY_FORM = { title: "", description: "", displayOrder: 0, linkUrl: "" };

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function SliderPage() {
  const [sliders, setSliders] = useState<ISlider[]>([]);
  const [loading, setLoading] = useState(true);

  /* ── Add modal ── */
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addImage, setAddImage] = useState<File | null>(null);
  const [addPreview, setAddPreview] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);
  const addFileRef = useRef<HTMLInputElement>(null);

  /* ── Edit modal ── */
  const [editSlider, setEditSlider] = useState<ISlider | null>(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", displayOrder: 0, isEnabled: true, linkUrl: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    getSliders()
      .then((r) => setSliders(r.data ?? []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  /* ── Add handlers ── */
  const onAddImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAddImage(file);
    setAddPreview(URL.createObjectURL(file));
  };

  const handleAddSave = () => {
    if (!addImage) return;
    setAddSaving(true);
    postSlider({ image: addImage, ...addForm })
      .then(() => { setAddOpen(false); load(); })
      .finally(() => setAddSaving(false));
  };

  const resetAdd = () => {
    setAddForm(EMPTY_FORM);
    setAddImage(null);
    setAddPreview(null);
  };

  /* ── Edit handlers ── */
  const openEdit = (s: ISlider) => {
    setEditSlider(s);
    setEditForm({ title: s.title, description: s.description, displayOrder: s.displayOrder, isEnabled: s.isEnabled, linkUrl: s.linkUrl ?? "" });
    setConfirmDelete(false);
  };

  const handleEditSave = () => {
    if (!editSlider) return;
    setEditSaving(true);
    putSlider(editSlider.id, editForm)
      .then(() => { setEditSlider(null); load(); })
      .finally(() => setEditSaving(false));
  };

  const handleDelete = () => {
    if (!editSlider) return;
    setDeleting(true);
    deleteSlider(editSlider.id)
      .then(() => { setEditSlider(null); setConfirmDelete(false); load(); })
      .finally(() => setDeleting(false));
  };

  return (
    <div className="container py-4" style={{ maxWidth: "1000px" }}>

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <div style={{ width: 5, height: 32, background: "#6366f1", borderRadius: 4, flexShrink: 0 }} />
          <div>
            <h4 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
              <i className="bi bi-images me-2 text-primary"></i>Slider Yönetimi
            </h4>
            <div className="text-muted" style={{ fontSize: "0.78rem" }}>Uygulama slider görsellerini yönetin</div>
          </div>
          <span className="badge" style={{ background: "#eff6ff", color: "#1d4ed8", fontSize: "0.78rem", borderRadius: 20, padding: "4px 12px" }}>
            {sliders.length} slider
          </span>
        </div>
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          style={{ borderRadius: 10, fontWeight: 600 }}
          onClick={() => { resetAdd(); setAddOpen(true); }}
        >
          <i className="bi bi-plus-lg"></i> Yeni Resim Ekle
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-5 text-muted">
          <div className="spinner-border text-primary" />
          <div className="mt-2 small">Yükleniyor...</div>
        </div>
      ) : sliders.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-images" style={{ fontSize: "2.5rem", opacity: 0.3 }}></i>
          <div className="mt-2">Henüz slider eklenmemiş.</div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {sliders
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((s) => (
              <div
                key={s.id}
                className="rounded-3 overflow-hidden"
                style={{ border: "1.5px solid #e9ecef", background: "#fff", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}
              >
                <div className="d-flex align-items-stretch flex-wrap">
                  {/* Görsel */}
                  <div style={{ width: 160, flexShrink: 0, background: "#f1f5f9", position: "relative", minHeight: 110 }}>
                    <img
                      src={s.imageUrl}
                      alt={s.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", minHeight: 110 }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = ""; (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                    />
                    <span
                      style={{
                        position: "absolute", top: 8, left: 8,
                        background: "#1e293b99", color: "#fff",
                        fontSize: "0.68rem", fontWeight: 700,
                        borderRadius: 8, padding: "2px 8px",
                      }}
                    >
                      #{s.displayOrder}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-grow-1 p-3 d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <span className="fw-bold" style={{ color: "#1e293b", fontSize: "0.97rem" }}>{s.title || <span className="text-muted fst-italic">Başlık yok</span>}</span>
                        <span
                          style={{
                            fontSize: "0.68rem", fontWeight: 700, borderRadius: 20, padding: "2px 9px",
                            background: s.isEnabled ? "#f0fdf4" : "#fef2f2",
                            color: s.isEnabled ? "#16a34a" : "#dc2626",
                            border: `1px solid ${s.isEnabled ? "#bbf7d0" : "#fecaca"}`,
                          }}
                        >
                          {s.isEnabled ? "Aktif" : "Pasif"}
                        </span>
                      </div>
                      {s.description && (
                        <div className="text-muted" style={{ fontSize: "0.83rem", lineHeight: 1.45 }}>{s.description}</div>
                      )}
                      {s.linkUrl && (
                        <div className="mt-1" style={{ fontSize: "0.75rem", color: "#6366f1" }}>
                          <i className="bi bi-link-45deg me-1"></i>
                          <span style={{ wordBreak: "break-all" }}>{s.linkUrl}</span>
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                      <span className="text-muted" style={{ fontSize: "0.73rem" }}>
                        <i className="bi bi-calendar3 me-1"></i>{formatDate(s.createdAt)}
                      </span>
                      <button
                        className="btn btn-sm"
                        style={{ borderRadius: 8, border: "1.5px solid #e0e7ff", background: "#f8faff", color: "#4f46e5", fontWeight: 600, fontSize: "0.8rem" }}
                        onClick={() => openEdit(s)}
                      >
                        <i className="bi bi-pencil me-1"></i>Düzenle
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* ── Add Modal ── */}
      <Modal show={addOpen} onHide={() => setAddOpen(false)} centered>
        <Modal.Header closeButton style={{ background: "#eff6ff", borderBottom: "1.5px solid #bfdbfe" }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700, color: "#1e40af" }}>
            <i className="bi bi-plus-circle-fill me-2"></i>Yeni Slider Ekle
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="d-flex flex-column gap-3">

            {/* Görsel */}
            <div>
              <label className="form-label small fw-semibold text-muted mb-1">
                <i className="bi bi-image me-1"></i>Görsel <span className="text-danger">*</span>
              </label>
              {addPreview ? (
                <div className="position-relative mb-2" style={{ borderRadius: 10, overflow: "hidden", border: "1.5px solid #e9ecef", maxHeight: 180 }}>
                  <img src={addPreview} alt="preview" style={{ width: "100%", height: 180, objectFit: "cover" }} />
                  <button
                    type="button"
                    onClick={() => { setAddImage(null); setAddPreview(null); if (addFileRef.current) addFileRef.current.value = ""; }}
                    style={{ position: "absolute", top: 8, right: 8, background: "#ef4444", border: "none", borderRadius: "50%", width: 28, height: 28, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem" }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => addFileRef.current?.click()}
                  style={{ border: "2px dashed #bfdbfe", borderRadius: 10, padding: "28px", textAlign: "center", cursor: "pointer", background: "#f8faff", color: "#6b7280" }}
                >
                  <i className="bi bi-cloud-upload" style={{ fontSize: "1.8rem", color: "#93c5fd" }}></i>
                  <div className="mt-1 small fw-semibold">Görsel seçmek için tıklayın</div>
                  <div style={{ fontSize: "0.72rem" }}>PNG, JPG desteklenir</div>
                </div>
              )}
              <input ref={addFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onAddImageChange} />
            </div>

            {/* Title */}
            <div>
              <label className="form-label small fw-semibold text-muted mb-1">
                <i className="bi bi-type me-1"></i>Başlık
              </label>
              <input
                type="text"
                className="form-control"
                style={{ borderRadius: 10, border: "1.5px solid #dee2e6" }}
                placeholder="Slider başlığı"
                value={addForm.title}
                onChange={(e) => setAddForm(p => ({ ...p, title: e.target.value }))}
              />
            </div>

            {/* Description */}
            <div>
              <label className="form-label small fw-semibold text-muted mb-1">
                <i className="bi bi-text-left me-1"></i>Açıklama
              </label>
              <textarea
                className="form-control"
                style={{ borderRadius: 10, border: "1.5px solid #dee2e6", resize: "none" }}
                rows={2}
                placeholder="Slider açıklaması"
                value={addForm.description}
                onChange={(e) => setAddForm(p => ({ ...p, description: e.target.value }))}
              />
            </div>

            {/* Display Order */}
            <div>
              <label className="form-label small fw-semibold text-muted mb-1">
                <i className="bi bi-sort-numeric-down me-1"></i>Sıra
              </label>
              <input
                type="number"
                className="form-control"
                style={{ borderRadius: 10, border: "1.5px solid #dee2e6", width: 120 }}
                min={0}
                value={addForm.displayOrder}
                onChange={(e) => setAddForm(p => ({ ...p, displayOrder: Number(e.target.value) }))}
              />
              <div className="mt-1" style={{ fontSize: "0.72rem", color: "#6b7280" }}>
                <i className="bi bi-info-circle me-1"></i>Küçük sayı öne çıkar
              </div>
            </div>

            {/* Link URL */}
            <div>
              <label className="form-label small fw-semibold text-muted mb-1">
                <i className="bi bi-link-45deg me-1"></i>Link URL <span className="text-muted fw-normal">(opsiyonel)</span>
              </label>
              <input
                type="url"
                className="form-control"
                style={{ borderRadius: 10, border: "1.5px solid #dee2e6" }}
                placeholder="https://..."
                value={addForm.linkUrl}
                onChange={(e) => setAddForm(p => ({ ...p, linkUrl: e.target.value }))}
              />
              <div className="mt-1" style={{ fontSize: "0.72rem", color: "#6b7280" }}>
                <i className="bi bi-info-circle me-1"></i>Slider'a tıklandığında açılacak sayfa
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: "#f9fafb", borderTop: "1.5px solid #e5e7eb" }}>
          <button
            className="btn"
            style={{ borderRadius: 10, background: "#f3f4f6", color: "#374151", fontWeight: 600, border: "1.5px solid #e5e7eb" }}
            onClick={() => setAddOpen(false)}
          >
            İptal
          </button>
          <button
            className="btn btn-primary"
            style={{ borderRadius: 10, fontWeight: 600 }}
            onClick={handleAddSave}
            disabled={addSaving || !addImage}
          >
            {addSaving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg me-1"></i>Kaydet</>}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal show={!!editSlider} onHide={() => setEditSlider(null)} centered>
        <Modal.Header closeButton style={{ background: "#fffbeb", borderBottom: "1.5px solid #fde68a" }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700, color: "#92400e" }}>
            <i className="bi bi-pencil-fill me-2"></i>Slider Düzenle
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {editSlider && (
            <div className="d-flex flex-column gap-3">

              {/* Mevcut görsel */}
              <div className="rounded-3 overflow-hidden" style={{ border: "1.5px solid #e9ecef", maxHeight: 160 }}>
                <img src={editSlider.imageUrl} alt={editSlider.title} style={{ width: "100%", height: 160, objectFit: "cover" }} />
              </div>
              <div className="small text-muted" style={{ marginTop: -8 }}>
                <i className="bi bi-info-circle me-1"></i>Görseli değiştirmek için yeni slider ekleyin, eskisini silin.
              </div>

              {/* Title */}
              <div>
                <label className="form-label small fw-semibold text-muted mb-1">
                  <i className="bi bi-type me-1"></i>Başlık
                </label>
                <input
                  type="text"
                  className="form-control"
                  style={{ borderRadius: 10, border: "1.5px solid #dee2e6" }}
                  value={editForm.title}
                  onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))}
                />
              </div>

              {/* Description */}
              <div>
                <label className="form-label small fw-semibold text-muted mb-1">
                  <i className="bi bi-text-left me-1"></i>Açıklama
                </label>
                <textarea
                  className="form-control"
                  style={{ borderRadius: 10, border: "1.5px solid #dee2e6", resize: "none" }}
                  rows={2}
                  value={editForm.description}
                  onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))}
                />
              </div>

              {/* Display Order + isEnabled */}
              <div className="d-flex gap-3 align-items-end flex-wrap">
                <div>
                  <label className="form-label small fw-semibold text-muted mb-1">
                    <i className="bi bi-sort-numeric-down me-1"></i>Sıra
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    style={{ borderRadius: 10, border: "1.5px solid #dee2e6", width: 100 }}
                    min={0}
                    value={editForm.displayOrder}
                    onChange={(e) => setEditForm(p => ({ ...p, displayOrder: Number(e.target.value) }))}
                  />
                </div>
                <div
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                  style={{ background: editForm.isEnabled ? "#f0fdf4" : "#fef2f2", border: `1.5px solid ${editForm.isEnabled ? "#bbf7d0" : "#fecaca"}`, cursor: "pointer" }}
                  onClick={() => setEditForm(p => ({ ...p, isEnabled: !p.isEnabled }))}
                >
                  <div
                    style={{
                      width: 36, height: 20, borderRadius: 20, position: "relative", transition: "background 0.2s",
                      background: editForm.isEnabled ? "#16a34a" : "#d1d5db",
                    }}
                  >
                    <div style={{
                      position: "absolute", top: 2, left: editForm.isEnabled ? 18 : 2,
                      width: 16, height: 16, borderRadius: "50%", background: "#fff",
                      transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }} />
                  </div>
                  <span className="small fw-semibold" style={{ color: editForm.isEnabled ? "#16a34a" : "#6b7280" }}>
                    {editForm.isEnabled ? "Aktif" : "Pasif"}
                  </span>
                </div>
              </div>

              {/* Link URL */}
              <div>
                <label className="form-label small fw-semibold text-muted mb-1">
                  <i className="bi bi-link-45deg me-1"></i>Link URL <span className="text-muted fw-normal">(opsiyonel)</span>
                </label>
                <input
                  type="url"
                  className="form-control"
                  style={{ borderRadius: 10, border: "1.5px solid #dee2e6" }}
                  placeholder="https://..."
                  value={editForm.linkUrl}
                  onChange={(e) => setEditForm(p => ({ ...p, linkUrl: e.target.value }))}
                />
                <div className="mt-1" style={{ fontSize: "0.72rem", color: "#6b7280" }}>
                  <i className="bi bi-info-circle me-1"></i>Slider'a tıklandığında açılacak sayfa
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "#f9fafb", borderTop: "1.5px solid #e5e7eb", flexDirection: "column", alignItems: "stretch", gap: 8 }}>
          {confirmDelete ? (
            <div className="d-flex align-items-center justify-content-between gap-2 px-3 py-2 rounded-3" style={{ background: "#fef2f2", border: "1px solid #fecaca" }}>
              <span className="small fw-semibold" style={{ color: "#991b1b" }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>Bu slider'ı silmek istediğinizden emin misiniz?
              </span>
              <div className="d-flex gap-2">
                <button className="btn btn-sm" style={{ borderRadius: 8, background: "#f3f4f6", border: "1.5px solid #e5e7eb", fontWeight: 600 }} onClick={() => setConfirmDelete(false)} disabled={deleting}>Hayır</button>
                <button className="btn btn-sm btn-danger" style={{ borderRadius: 8, fontWeight: 600 }} onClick={handleDelete} disabled={deleting}>
                  {deleting ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-trash-fill me-1"></i>Evet, Sil</>}
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-center">
              <button
                className="btn btn-sm"
                style={{ borderRadius: 8, background: "#fef2f2", color: "#991b1b", fontWeight: 600, border: "1.5px solid #fecaca" }}
                onClick={() => setConfirmDelete(true)}
              >
                <i className="bi bi-trash me-1"></i>Sil
              </button>
              <div className="d-flex gap-2">
                <button className="btn" style={{ borderRadius: 10, background: "#f3f4f6", color: "#374151", fontWeight: 600, border: "1.5px solid #e5e7eb" }} onClick={() => setEditSlider(null)}>
                  İptal
                </button>
                <button className="btn btn-primary" style={{ borderRadius: 10, fontWeight: 600 }} onClick={handleEditSave} disabled={editSaving}>
                  {editSaving ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg me-1"></i>Kaydet</>}
                </button>
              </div>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
